import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';

const CATEGORY_LABELS = {
  broadband_only: 'Velocity Series - Broadband Only',
  broadband_iptv: 'Vista Series - Broadband + IPTV',
  broadband_ott: 'Pulse Series - Broadband + OTT',
  broadband_iptv_ott: 'Infinity Series - Broadband + IPTV + OTT'
};

export const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await api.getPlans();
      setPlans(data);
    } catch (err) {
      setError('Failed to load plans. Please try again later.');
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const groupedPlans = plans.reduce((acc, plan) => {
    if (!acc[plan.category]) {
      acc[plan.category] = [];
    }
    acc[plan.category].push(plan);
    return acc;
  }, {});

  const filteredCategories = selectedCategory === 'all' 
    ? Object.keys(groupedPlans) 
    : [selectedCategory];

  if (loading) {
    return (
      <div className="min-h-screen pt-20">
        <div className="container mx-auto px-4 py-20 text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-red-600" />
          <p className="mt-4 text-gray-600">Loading plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20">
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-red-600">{error}</p>
          <Button onClick={fetchPlans} className="mt-4 bg-red-600 hover:bg-red-700">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Choose Your Perfect <span className="text-red-600">Plan</span>
            </h1>
            <p className="text-xl text-gray-600">
              Flexible plans designed to meet your connectivity needs
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 bg-white border-b">
        <div className="container mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              onClick={() => setSelectedCategory('all')}
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              className={selectedCategory === 'all' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              All Plans
            </Button>
            {Object.keys(groupedPlans).map((category) => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={selectedCategory === category ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                {CATEGORY_LABELS[category]}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Plans by Category */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto space-y-16">
          {filteredCategories.map((category) => {
            const categoryPlans = groupedPlans[category] || [];
            const popularPlan = categoryPlans.find(p => p.speed_mbps === 100);
            
            return (
              <div key={category}>
                <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                  {CATEGORY_LABELS[category]}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                  {categoryPlans.map((plan) => {
                    const isPopular = plan.id === popularPlan?.id;
                    
                    return (
                      <Card
                        key={plan.id}
                        className={`relative hover:shadow-2xl transition-all duration-300 ${
                          isPopular
                            ? 'border-2 border-red-600 transform lg:-translate-y-4'
                            : 'hover:border-red-300'
                        }`}
                      >
                        {isPopular && (
                          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                            <div className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                              <Sparkles size={14} />
                              <span>Popular</span>
                            </div>
                          </div>
                        )}

                        <CardHeader className="text-center pb-4">
                          <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                            {plan.name}
                          </CardTitle>
                          <div className="space-y-2">
                            <div>
                              <div className="text-3xl font-bold text-red-600">{plan.speed_mbps} Mbps</div>
                              <div className="text-sm text-gray-600">Speed</div>
                            </div>
                            <div>
                              <div className="text-2xl font-semibold text-gray-700">
                                ₹{plan.yearly_price.toLocaleString()}/yr
                              </div>
                              <div className="text-xs text-gray-500">
                                ₹{plan.monthly_price}/mo
                              </div>
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent>
                          <ul className="space-y-3 mb-6">
                            {plan.features.map((feature, idx) => (
                              <li key={idx} className="flex items-start space-x-3">
                                <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Check size={14} className="text-red-600" />
                                </div>
                                <span className="text-sm text-gray-700">{feature}</span>
                              </li>
                            ))}
                          </ul>

                          <Link to="/contact">
                            <Button
                              className={`w-full ${
                                isPopular
                                  ? 'bg-red-600 hover:bg-red-700 text-white'
                                  : 'bg-white border-2 border-red-600 text-red-600 hover:bg-red-50'
                              }`}
                            >
                              Get Started
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">All Plans Include</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Premium features with every plan
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              'Free professional installation',
              'No hidden charges',
              'Free router (on select plans)',
              '99.9% uptime guarantee',
              '24/7 customer support',
              'Easy bill payment options',
              'Flexible upgrade options',
              'Roaming WiFi access',
              'Mobile app for easy management'
            ].map((feature, idx) => (
              <div key={idx} className="flex items-start space-x-3 bg-white p-4 rounded-lg">
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={16} className="text-red-600" />
                </div>
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-red-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Need Help Choosing?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our team is here to help you find the perfect plan for your needs
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Our Team
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
